import { createFileRoute, useRouter } from '@tanstack/react-router'
import {
  Badge,
  Button,
  Card,
  Content as CardContent,
  Text as CardDescription,
  Header as CardHeader,
  Heading as CardTitle,
  Checkbox,
  CustomDialog,
  DialogContainer,
  TextField as Input,
  LinkButton,
  Picker,
  PickerItem,
} from '@react-spectrum/s2'
import { useState } from 'react'
import {
  ArrowLeft,
  Edit,
  Laptop,
  Loader2,
  Shield,
  Trash2,
  UserCog,
} from 'lucide-react'
import {
  adminRemoveUser,
  adminRevokeSession,
  getAdminUserDetails,
  getGlobalPlans,
  updateAdminUserDetails,
} from '@rpc/admin.rpc'
import { authClient } from '@client/auth-client'
import { APP_ROUTES } from '@shared/app-routes'
import { fullWidthStyles } from '@/styles/s2-macros'

export const Route = createFileRoute('/admin/users/$userId')({
  loader: async ({ params }) => {
    return {
      data: await getAdminUserDetails({ data: { userId: params.userId } }),
      plans: await getGlobalPlans(),
    }
  },
  component: UserDetailsPage,
})

function UserDetailsPage() {
  const {
    data: { user, sessions: rawSessions, memberships },
    plans,
  } = Route.useLoaderData()
  // Normalize sessions
  const sessions = Array.isArray(rawSessions)
    ? rawSessions
    : rawSessions?.sessions || []

  const router = useRouter()
  const [isProcessing, setIsProcessing] = useState(false)

  // Edit State
  const [isEditOpen, setIsEditOpen] = useState(false)
  const [editForm, setEditForm] = useState({
    name: user.name,
    email: user.email,
    role: user.role || 'user',
    billingPlan: user.billingPlan || 'registeredUser',
    emailVerified: user.emailVerified,
    banned: user.banned || false,
  })
  const billingPlanOptions = getBillingPlanOptions(plans, editForm.billingPlan)

  const handleRevokeSession = async (sessionToken: string) => {
    if (!confirm('Revoke this session?')) return
    setIsProcessing(true)
    try {
      await adminRevokeSession({ data: { sessionToken } })
      router.invalidate()
    } catch (e) {
      alert('Failed to revoke session')
    } finally {
      setIsProcessing(false)
    }
  }

  const handleImpersonate = async () => {
    setIsProcessing(true)
    try {
      await authClient.admin.impersonateUser({ userId: user.id })
      window.location.href = APP_ROUTES.dashboard.home
    } catch (e) {
      alert('Impersonation failed')
      setIsProcessing(false)
    }
  }

  const handleDeleteUser = async () => {
    if (
      !confirm(
        'DANGER: This will permanently delete the user and all their data. Continue?',
      )
    )
      return
    setIsProcessing(true)
    try {
      await adminRemoveUser({ data: { userId: user.id } })
      router.navigate({ to: '/admin/users' })
    } catch (e) {
      alert('Failed to delete user')
      setIsProcessing(false)
    }
  }

  const handleSaveUser = async (e: React.FormEvent) => {
    e.preventDefault()
    setIsProcessing(true)
    try {
      await updateAdminUserDetails({
        data: {
          userId: user.id,
          data: editForm,
        },
      })
      setIsEditOpen(false)
      router.invalidate()
    } catch (error) {
      alert('Failed to update user')
    } finally {
      setIsProcessing(false)
    }
  }

  return (
    <div className="mx-auto w-full max-w-5xl space-y-8 px-4 py-8 sm:px-6 lg:px-8">
      <div className="flex flex-wrap items-center gap-4">
        <LinkButton
          href="/admin/users"
          variant="secondary"
          fillStyle="outline"
          size="S"
          aria-label="Back to users"
        >
          <ArrowLeft className="h-4 w-4" />
        </LinkButton>
        <div>
          <h2 className="text-3xl font-bold text-neutral-900 dark:text-neutral-100">
            {user.name}
          </h2>
          <p className="text-neutral-500 font-mono text-sm">{user.id}</p>
        </div>
        <div className="ml-auto flex gap-2">
          <Button
            variant="secondary"
            fillStyle="outline"
            onPress={handleImpersonate}
            isDisabled={isProcessing}
          >
            <UserCog className="h-4 w-4 mr-2" />
            Impersonate
          </Button>
          <Button
            variant="negative"
            onPress={handleDeleteUser}
            isDisabled={isProcessing}
          >
            <Trash2 className="h-4 w-4 mr-2" />
            Delete user
          </Button>
        </div>
      </div>

      <div className="grid gap-6 md:grid-cols-3">
        {/* Profile Card */}
        <Card styles={fullWidthStyles}>
          <CardHeader>
            <div className="flex items-center justify-between pb-2">
              <CardTitle>Profile</CardTitle>
              <Button
                variant="secondary"
                fillStyle="outline"
                size="S"
                aria-label="Edit user profile"
                onPress={() => setIsEditOpen(true)}
              >
                <Edit className="h-4 w-4 text-neutral-500" />
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            <div className="space-y-4 pt-6">
              <div className="mb-4 flex justify-center">
                {user.image ? (
                  <img
                    src={user.image}
                    alt={`${user.name} avatar`}
                    className="h-24 w-24 rounded-full"
                  />
                ) : (
                  <div className="h-24 w-24 rounded-full bg-neutral-100 flex items-center justify-center text-3xl font-bold text-neutral-400">
                    {user.name.charAt(0)}
                  </div>
                )}
              </div>
              <div className="space-y-1">
                <label className="text-xs font-bold uppercase text-neutral-500">
                  Email
                </label>
                <p className="font-medium">{user.email}</p>
                {user.emailVerified ? (
                  <Badge variant="positive" fillStyle="subtle">
                    Verified
                  </Badge>
                ) : (
                  <Badge variant="notice" fillStyle="subtle">
                    Unverified
                  </Badge>
                )}
              </div>
              <div className="space-y-1">
                <label className="text-xs font-bold uppercase text-neutral-500">
                  Auth role
                </label>
                <p>{formatAuthRoleLabel(user.role)}</p>
                <p className="text-xs text-neutral-500">
                  Controls admin access and account permissions.
                </p>
              </div>
              <div className="space-y-1">
                <label className="text-xs font-bold uppercase text-neutral-500">
                  Billing plan
                </label>
                <p>{formatBillingPlanLabel(user.billingPlan)}</p>
                <p className="text-xs text-neutral-500">
                  Controls usage limits and subscription entitlements.
                </p>
              </div>
              <div className="space-y-1">
                <label className="text-xs font-bold uppercase text-neutral-500">
                  Joined
                </label>
                <p>{new Date(user.createdAt).toLocaleDateString()}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <div className="md:col-span-2 space-y-6">
          {/* Organizations */}
          <Card styles={fullWidthStyles}>
            <CardHeader>
              <CardTitle>Organizations</CardTitle>
              <CardDescription>Teams this user is a member of.</CardDescription>
            </CardHeader>
            <CardContent>
              {memberships.length === 0 ? (
                <p className="text-sm text-neutral-500 italic">
                  No organization memberships.
                </p>
              ) : (
                <div className="space-y-2">
                  {memberships.map((m: any) => (
                    <div
                      key={m.orgId}
                      className="flex items-center justify-between rounded-lg border border-neutral-200/80 p-3 transition-colors hover:bg-neutral-50/70 dark:border-neutral-800/80 dark:hover:bg-neutral-900/40"
                    >
                      <div className="flex items-center gap-3">
                        <div className="h-8 w-8 rounded bg-blue-100 flex items-center justify-center text-blue-600">
                          <Shield className="h-4 w-4" />
                        </div>
                        <div>
                          <p className="font-medium">{m.name}</p>
                          <p className="text-xs text-neutral-500">
                            Joined {new Date(m.joinedAt).toLocaleDateString()}
                          </p>
                        </div>
                      </div>
                      <Badge variant="accent" fillStyle="subtle">
                        {formatOrgRoleLabel(m.role)}
                      </Badge>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Sessions */}
          <Card styles={fullWidthStyles}>
            <CardHeader>
              <CardTitle>Active sessions</CardTitle>
              <CardDescription>
                Manage active logins for this user.
              </CardDescription>
            </CardHeader>
            <CardContent>
              {sessions.length === 0 ? (
                <p className="text-sm text-neutral-500 italic">
                  No active sessions.
                </p>
              ) : (
                <div className="space-y-2">
                  {sessions.map((s: any) => (
                    <div
                      key={s.id}
                      className="flex items-center justify-between rounded-lg border border-neutral-200/80 bg-white p-3 dark:border-neutral-800/80 dark:bg-neutral-900"
                    >
                      <div className="flex items-center gap-3">
                        <div className="h-8 w-8 rounded bg-green-100 flex items-center justify-center text-green-600">
                          <Laptop className="h-4 w-4" />
                        </div>
                        <div>
                          <p className="font-medium text-sm">
                            {s.userAgent || 'Unknown device'}
                          </p>
                          <p className="text-xs text-neutral-500">
                            {s.ipAddress} • Expires{' '}
                            {new Date(s.expiresAt).toLocaleDateString()}
                          </p>
                        </div>
                      </div>
                      <Button
                        variant="negative"
                        fillStyle="outline"
                        size="S"
                        onPress={() => handleRevokeSession(s.token)}
                        isDisabled={isProcessing}
                      >
                        Revoke
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>

      {isEditOpen ? (
        <DialogContainer onDismiss={() => setIsEditOpen(false)}>
          <CustomDialog isDismissible aria-label="Edit user profile">
            <div className="space-y-4">
              <h2 className="text-lg font-semibold">Edit user profile</h2>
              <p className="text-sm text-neutral-600 dark:text-neutral-300">
                Make changes to user details, auth role, and billing plan.
              </p>
              <form onSubmit={handleSaveUser} className="space-y-4 py-2">
                <div className="grid gap-2">
                  <label>Full name</label>
                  <Input
                    aria-label="Full name"
                    value={editForm.name}
                    onChange={(value) =>
                      setEditForm({ ...editForm, name: value })
                    }
                  />
                </div>
                <div className="grid gap-2">
                  <label>Email</label>
                  <Input
                    aria-label="Email address"
                    value={editForm.email}
                    onChange={(value) =>
                      setEditForm({ ...editForm, email: value })
                    }
                  />
                </div>
                <div className="grid gap-2">
                  <label>Auth role</label>
                  <Picker
                    aria-label="Auth role"
                    placeholder="Select auth role"
                    selectedKey={editForm.role}
                    onSelectionChange={(key) =>
                      setEditForm({ ...editForm, role: String(key) })
                    }
                  >
                    <PickerItem id="user">User</PickerItem>
                    <PickerItem id="admin">Admin</PickerItem>
                  </Picker>
                </div>
                <div className="grid gap-2">
                  <label>Billing plan</label>
                  <Picker
                    aria-label="Billing plan"
                    placeholder="Select billing plan"
                    selectedKey={editForm.billingPlan}
                    onSelectionChange={(key) =>
                      setEditForm({ ...editForm, billingPlan: String(key) })
                    }
                  >
                    {billingPlanOptions.map((plan) => (
                      <PickerItem key={plan.id} id={plan.id}>
                        {plan.label}
                      </PickerItem>
                    ))}
                  </Picker>
                </div>
                <div className="flex items-center justify-between rounded-lg border p-3 shadow-sm">
                  <div className="space-y-0.5">
                    <label className="text-base">Email verified</label>
                  </div>
                  <Checkbox
                    aria-label="Email verified"
                    isSelected={editForm.emailVerified}
                    onChange={(c) =>
                      setEditForm({ ...editForm, emailVerified: !!c })
                    }
                  />
                </div>
                <div className="flex items-center justify-between rounded-lg border border-red-100 bg-red-50/50 p-3 shadow-sm">
                  <div className="space-y-0.5">
                    <label className="text-base text-red-600">Banned</label>
                    <p className="text-xs text-red-500">
                      Prevent this user from logging in.
                    </p>
                  </div>
                  <Checkbox
                    aria-label="Banned"
                    isSelected={editForm.banned}
                    onChange={(c) => setEditForm({ ...editForm, banned: !!c })}
                  />
                </div>
                <div className="flex justify-end">
                  <Button type="submit" isDisabled={isProcessing}>
                    {isProcessing ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      'Save changes'
                    )}
                  </Button>
                </div>
              </form>
            </div>
          </CustomDialog>
        </DialogContainer>
      ) : null}
    </div>
  )
}

function formatAuthRoleLabel(role?: string | null) {
  if (!role) return 'User'
  return role.charAt(0).toUpperCase() + role.slice(1)
}

function formatOrgRoleLabel(role?: string | null) {
  if (!role) return 'Member'
  return role.charAt(0).toUpperCase() + role.slice(1)
}

function formatBillingPlanLabel(plan?: string | null) {
  if (!plan) return 'Free'
  return plan.replace('User', '').replace(/^./, (char) => char.toUpperCase())
}

export function getBillingPlanOptions(
  plans: Array<{
    name?: string | null
    displayName?: string | null
    availableToAuthenticated?: boolean | null
  }>,
  currentPlan?: string | null,
) {
  const options = plans
    .filter((plan) => plan.availableToAuthenticated === true)
    .map((plan) => plan.name?.trim())
    .filter((name): name is string => Boolean(name))
    .map((name) => ({
      id: name,
      label: formatBillingPlanLabel(name),
    }))

  if (currentPlan && !options.some((plan) => plan.id === currentPlan)) {
    options.unshift({
      id: currentPlan,
      label: formatBillingPlanLabel(currentPlan),
    })
  }

  return options
}
